import { FC, useState, useRef, useEffect } from 'react'

import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import OutsideClickHandler from 'react-outside-click-handler'

import ErrorIcon from 'src/Assets/Svg/common/error.svg?react'
import CloseIcon from 'src/Assets/Svg/common/cross.svg?react'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'

import { PrimaryInput } from '../PrimaryInput'
import { ArtistItem, ArtistItemData } from '../ArtistItem'

import { MobileArtistSearch } from './MobileArtistSearch'

import { ArtistsScrollList } from './styles'

const DEBOUNCE_DELAY = 1000

const ArtistsContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 480px;
  padding: 8px 0;
  box-shadow: ${({ theme }) => theme.shadow.card} !important;
  border-radius: 16px !important;
  border: ${({ theme }) => `1px solid ${theme.color.lightGrey}`};
  overflow: hidden;
  position: absolute;
  top: calc(100% + 8px);
  z-index: 3;
  background-color: ${({ theme }) => theme.color.white};
  width: 100%;
`

const ScrollList = styled(ArtistsScrollList)`
  padding: 0 8px;
`

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .ant-input-affix-wrapper {
    padding-right: 44px !important;
  }

  .ant-input-affix-wrapper .ant-input-suffix {
    position: absolute;
    top: 50%;
    right: 4px;
    transform: translateY(-50%);
    margin-top: 0;
  }
`

const EmptyState = styled.div<{
  isError?: boolean
}>`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 24px 12px;
  border-radius: 16px;
  border: ${({ theme }) => `1px solid ${theme.color.lightGrey}`};
  box-shadow: ${({ theme }) => theme.shadow.header};
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme, isError }) =>
    isError ? theme.color.mediumRed : theme.color.darkGrey};
`

export interface DropdownArtistsProps {
  searchedArtists?: Array<ArtistItemData> | null
  selectedArtist?: ArtistItemData
  searchValue?: string
  onSearchChange?: (value: string) => void
  isLoading?: boolean
  error?: string
  placeholder?: string
}

const EMPTY_STATE_MESSAGE = 'Artist is not found'
const DEFAULT_PLACEHOLDER = 'Artist name or Spotify artist URL (Optional)'
const MAX_SEARCH_INPUT_LENGTH = 255

const shouldShowEmptyState = (
  inputValue: string,
  searchedArtists: ArtistItemData[] | null | undefined,
  isLoading: boolean | undefined,
  error: string | undefined,
): boolean => {
  if (!inputValue || isLoading || error) {
    return false
  }
  return Array.isArray(searchedArtists) && searchedArtists.length === 0
}

const renderArtistsList = (artists: ArtistItemData[]): JSX.Element => (
  <ArtistsContainer>
    <ScrollList>
      {artists.map((artist, index) => (
        <ArtistItem key={`${artist.artistName}-${index}`} {...artist} />
      ))}
    </ScrollList>
  </ArtistsContainer>
)

const renderErrorState = (message: string): JSX.Element => (
  <EmptyState isError={true}>
    <ErrorIcon />
    <span>{message}</span>
  </EmptyState>
)

export const DropdownArtists: FC<DropdownArtistsProps> = ({
  searchedArtists,
  selectedArtist,
  searchValue,
  onSearchChange,
  isLoading,
  error,
  placeholder = DEFAULT_PLACEHOLDER,
}) => {
  const [inputValue, setInputValue] = useState(searchValue || '')
  const [prevSearchValue, setPrevSearchValue] = useState(searchValue)
  const [isListHidden, setIsListHidden] = useState(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMobile = useMediaQuery({ maxWidth: MAX_MOBILE_WIDTH })

  if (prevSearchValue !== searchValue) {
    setPrevSearchValue(searchValue)
    setInputValue(searchValue || '')
  }

  const handleInputChange = (value: string): void => {
    setInputValue(value)
    setIsListHidden(false)

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      onSearchChange?.(value)
    }, DEBOUNCE_DELAY)
  }

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    },
    [],
  )

  const showEmpty = shouldShowEmptyState(
    inputValue,
    searchedArtists,
    isLoading,
    error,
  )
  const hasArtists = searchedArtists && searchedArtists.length > 0

  if (isMobile) {
    return (
      <MobileArtistSearch
        placeholder={placeholder}
        searchedArtists={searchedArtists}
        selectedArtist={selectedArtist}
        isLoading={isLoading}
        error={error}
        value={inputValue}
        maxLength={MAX_SEARCH_INPUT_LENGTH}
        onChange={handleInputChange}
      />
    )
  }

  return (
    <Container>
      <PrimaryInput
        placeholder={placeholder}
        value={inputValue}
        maxLength={MAX_SEARCH_INPUT_LENGTH}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setIsListHidden(false)}
        autoComplete='off'
        allowClear={{ clearIcon: <CloseIcon /> }}
      />

      {hasArtists && !isListHidden && (
        <OutsideClickHandler onOutsideClick={() => setIsListHidden(true)}>
          {renderArtistsList(searchedArtists)}
        </OutsideClickHandler>
      )}
      {showEmpty && renderErrorState(EMPTY_STATE_MESSAGE)}
      {error && renderErrorState(error)}
    </Container>
  )
}
